import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { openModal } from "../../store/modal/modalReducer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSpinnerThird,
    faFilePdf,
    faFileImage,
    faFileWord,
    faFileExcel,
    faFileAlt,
    faFileArchive,
    faFileAudio,
    faFileVideo,
    faFileCsv,
    faTimes,
    faUserClock,
    faCheckCircle,
    faTimesCircle,
    faSync,
    faFile,
    faUser,
    faUserGroup,
    faBuilding,
    faList,
    faPaperPlane,
    faUpload,
    faRedo,
    faArrowLeft,
    faTrash,
    faCopy,
    faFileCirclePlus,
    faCalculator,
} from "@fortawesome/pro-solid-svg-icons";
import axios from "axios";
import moment from "moment";
import CustomButton from "../Helpers/CustomButton";
import FileSelectionModal from "../Modals/FileSelectionModal";
import CreditCheckQueue from "./CreditCheckQueue";
import { toast } from "react-toastify";
import NotificationToast from "../Helpers/NotificationToast";

const CreditCheck = ({ current_booking, current_staff }) => {
    const dispatch = useDispatch();
    const [tpnLoading, setTpnLoading] = useState(false);
    const [requestNotes, setRequestNotes] = useState({}); // Store notes per TPN ID
    const [showRequestNotes, setShowRequestNotes] = useState(null); // Store TPN ID of open form (null = all closed)
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [tpnRecords, setTpnRecords] = useState([]);
    const [loadingTpnRecords, setLoadingTpnRecords] = useState(true);
    const [checkingUpdates, setCheckingUpdates] = useState(false);
    const [activeTPN, setActiveTPN] = useState(null);
    const [showQueueModal, setShowQueueModal] = useState(false);
    const [showFileModal, setShowFileModal] = useState(false);
    const [fileModalContext, setFileModalContext] = useState(null);
    const [resendingApproval, setResendingApproval] = useState(false);
    const [movingToPending, setMovingToPending] = useState(false);
    const [movingToApproved, setMovingToApproved] = useState(false);
    const [movingToDenied, setMovingToDenied] = useState(false);
    const [deletingTPN, setDeletingTPN] = useState(false);
    const [cloningToPending, setCloningToPending] = useState(false);
    const [creatingManual, setCreatingManual] = useState(false);
    const [manualReportType, setManualReportType] = useState(0); // 0=Consumer A, 1=Consumer B, 2=Company

    // Generate prefilled approval request notes
    const generateApprovalNotes = (tpn) => {
        const user = current_booking?.user;
        const profile = user?.Profile;
        const affordability = user?.Affordability;
        const property = current_booking?.Property;
        const unitType = current_booking?.unit_type;
        const rates = current_booking?.rates;
        const latestRate = rates && rates.length > 0 ? rates[rates.length - 1] : null;

        // Get applicant name
        const name = `${user?.name || ""} ${user?.surname || ""}`.trim() || "";

        // Get property and unit type info
        const propertyName = property?.name || "";
        const unitTypeName = unitType?.name || "";

        // Get lease start month (e.g., "Jan")
        let leaseMonth = "";
        if (latestRate?.lease_start) {
            const leaseDate = new Date(latestRate.lease_start);
            leaseMonth = leaseDate.toLocaleString("en-US", { month: "short" });
        }

        // Build property/unit/lease line
        let propertyLine = "";
        if (propertyName || unitTypeName) {
            propertyLine = `${propertyName} ${unitTypeName}`.trim();
            if (leaseMonth) {
                propertyLine += ` & lease for ${leaseMonth}`;
            }
        }

        // Employment info - always show the line with placeholders
        const employer = profile?.employer || "";
        const occupation = profile?.occupation || "";
        let employmentLine = "Works at ";
        if (employer) {
            employmentLine += employer;
        }
        if (occupation) {
            employmentLine += ` as ${occupation}`;
        }
        employmentLine += " – to be verified";

        // Net income calculation (net_income minus expenses) - always show the line
        let netLeftLine = "Net left: ";
        if (affordability?.net_income) {
            const netIncome = parseFloat(affordability.net_income) || 0;
            const rent = parseFloat(affordability.rent) || 0;
            const carRepayments = parseFloat(affordability.car_repayments) || 0;
            const loanRepayments = parseFloat(affordability.loan_repayments) || 0;
            const otherExpenses = parseFloat(affordability.other_expenses) || 0;
            const netLeft = netIncome - rent - carRepayments - loanRepayments - otherExpenses;
            netLeftLine = `Net left: R${netLeft.toLocaleString()}`;
        }

        // Get staff member name
        const staffName = `${current_staff?.name || ""} ${current_staff?.surname || ""}`.trim() || "";
        const submittedByLine = staffName ? `Submitted by: ${staffName}` : "";

        // Build the template - TPN-specific fields left blank for manual entry
        const lines = [
            name,
            propertyLine,
            " - ", // Credit score grade (e.g., "A - EXCELLENT") - blank for manual entry
            " - TPN", // TPN status (e.g., "NO - TPN") - blank for manual entry
            " - Credit Payments", // Credit payments % - blank for manual entry
            " – Defaults", // Defaults count - blank for manual entry
            employmentLine,
            netLeftLine,
            submittedByLine,
        ].filter((line) => line); // Remove empty lines

        return lines.join("\n");
    };

    // Handle opening request notes with prefilled data
    const handleOpenRequestNotes = (tpnId) => {
        // Only prefill if notes don't already exist for this TPN
        if (!requestNotes[tpnId]) {
            const tpn = tpnRecords.find((t) => t.id === tpnId);
            if (tpn) {
                const prefilledNotes = generateApprovalNotes(tpn);
                setRequestNotes({ ...requestNotes, [tpnId]: prefilledNotes });
            }
        }
        setShowRequestNotes(tpnId);
    };

    // Open SlideoverFiles with auto-preview for a specific file
    const handleFilePreview = (fileId, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        dispatch(
            openModal({
                modal_type: "SlideoverFiles",
                modal_props: {
                    type: "booking",
                    current_booking: current_booking,
                    autoPreviewFileId: fileId,
                },
            }),
        );
    };

    // Resend approval message for in-progress TPN
    const resendApprovalMessage = async (tpnId) => {
        if (!window.confirm("Are you sure you want to resend the approval message?")) {
            return;
        }

        setResendingApproval(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}tpn-approval/${tpnId}/resend-approval`);

            if (response.data.success) {
                toast(<NotificationToast title="Success" message={response.data.message || "Approval message resent successfully"} type="success" />);
                await fetchTpnRecords();
            } else {
                throw new Error(response.data.message || "Failed to resend approval message");
            }
        } catch (error) {
            console.error("Error resending approval message:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to resend approval message"} type="error" />);
        } finally {
            setResendingApproval(false);
        }
    };

    // Clone an approved/denied TPN to pending for resubmission
    const cloneToPending = async (tpnId) => {
        if (!window.confirm("Are you sure you want to clone this credit check to pending? This will create a new record that can be submitted for approval.")) {
            return;
        }

        setCloningToPending(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}tpn-approval/${tpnId}/clone-to-pending`);

            if (response.data.success) {
                toast(<NotificationToast title="Success" message={response.data.message || "Credit check cloned to pending successfully"} type="success" />);
                await fetchTpnRecords();
            } else {
                throw new Error(response.data.message || "Failed to clone credit check");
            }
        } catch (error) {
            console.error("Error cloning to pending:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to clone credit check to pending"} type="error" />);
        } finally {
            setCloningToPending(false);
        }
    };

    // Create a manual credit check (blank TPN record)
    const createManualCreditCheck = async (reportType = 0) => {
        if (!current_booking?.user?.id || !current_booking?.id) {
            toast(<NotificationToast title="Error" message="Missing booking or user information" type="error" />);
            return;
        }

        setCreatingManual(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}tpn-approval/create-manual`, {
                user_id: current_booking.user.id,
                booking_id: current_booking.id,
                report_type: reportType,
            });

            if (response.data.success) {
                toast(<NotificationToast title="Success" message={response.data.message || "Manual credit check created successfully"} type="success" />);
                await fetchTpnRecords();
            } else {
                throw new Error(response.data.message || "Failed to create manual credit check");
            }
        } catch (error) {
            console.error("Error creating manual credit check:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to create manual credit check"} type="error" />);
        } finally {
            setCreatingManual(false);
        }
    };

    // Delete a pending TPN
    const deleteTPN = async (tpnId) => {
        if (!window.confirm("Are you sure you want to delete this credit check? This action cannot be undone.")) {
            return;
        }

        setDeletingTPN(true);

        try {
            const response = await axios.delete(`${process.env.REACT_APP_API_URL}tpn-approval/${tpnId}`);

            if (response.data.success) {
                toast(<NotificationToast title="Success" message="Credit check deleted successfully" type="success" />);
                await fetchTpnRecords();
            } else {
                throw new Error(response.data.message || "Failed to delete credit check");
            }
        } catch (error) {
            console.error("Error deleting TPN:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to delete credit check"} type="error" />);
        } finally {
            setDeletingTPN(false);
        }
    };

    // Move in-progress TPN back to pending
    const moveToPending = async (tpnId) => {
        if (!window.confirm("Are you sure you want to move this credit check back to pending? It will be reassigned to the next available approver.")) {
            return;
        }

        setMovingToPending(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}tpn-approval/${tpnId}/move-to-pending`);

            if (response.data.success) {
                toast(<NotificationToast title="Success" message={response.data.message || "Credit check moved back to pending"} type="success" />);
                await fetchTpnRecords();
            } else {
                throw new Error(response.data.message || "Failed to move credit check to pending");
            }
        } catch (error) {
            console.error("Error moving to pending:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to move credit check to pending"} type="error" />);
        } finally {
            setMovingToPending(false);
        }
    };

    // Move in-progress TPN to approved
    const moveToApproved = async (tpnId) => {
        if (!window.confirm("Are you sure you want to move this credit check to approved?")) {
            return;
        }

        setMovingToApproved(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}tpn-approval/${tpnId}/move-to-approved`);

            if (response.data.success) {
                toast(<NotificationToast title="Success" message={response.data.message || "Credit check moved to approved"} type="success" />);
                await fetchTpnRecords();
            } else {
                throw new Error(response.data.message || "Failed to move credit check to approved");
            }
        } catch (error) {
            console.error("Error moving to approved:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to move credit check to approved"} type="error" />);
        } finally {
            setMovingToApproved(false);
        }
    };

    // Move in-progress TPN to denied
    const moveToDenied = async (tpnId) => {
        if (!window.confirm("Are you sure you want to move this credit check to denied?")) {
            return;
        }

        setMovingToDenied(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}tpn-approval/${tpnId}/move-to-denied`);

            if (response.data.success) {
                toast(<NotificationToast title="Success" message={response.data.message || "Credit check moved to denied"} type="success" />);
                await fetchTpnRecords();
            } else {
                throw new Error(response.data.message || "Failed to move credit check to denied");
            }
        } catch (error) {
            console.error("Error moving to denied:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to move credit check to denied"} type="error" />);
        } finally {
            setMovingToDenied(false);
        }
    };

    // Remove file from TPN record
    const removeFile = async (fileId, tpnId) => {
        if (!tpnId) return;

        // Check if TPN status is 'pending'
        const tpnRecord = tpnRecords.find((t) => t.id === tpnId);
        if (tpnRecord && tpnRecord.status !== "pending") {
            toast(<NotificationToast title="Error" message="Files can only be removed from pending TPN records" type="error" />);
            return;
        }

        try {
            // Call the API to remove the file association
            const response = await axios.delete(`${process.env.REACT_APP_API_URL}tpn-approval/${tpnId}/files/${fileId}`, {
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
                },
                withCredentials: true,
            });

            if (response.data.success) {
                // Refresh TPN records to get the updated file associations
                await fetchTpnRecords();

                // Show success message
                toast(<NotificationToast title="Success" message="File removed successfully" type="success" />);
            } else {
                throw new Error(response.data.message || "Failed to remove file");
            }
        } catch (error) {
            console.error("Error removing file:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to remove file. Please try again."} type="error" />);
        }
    };
    // Fetch TPN records for the user
    const fetchTpnRecords = async () => {
        if (!current_booking?.user?.id) return;

        setLoadingTpnRecords(true);
        try {
            const params = new URLSearchParams();
            if (current_booking?.id) {
                params.append("booking_id", current_booking.id);
            }
            // Include attached_files in the response
            params.append("include", "attached_files");

            const response = await axios.get(`${process.env.REACT_APP_API_URL}tpn/user/${current_booking.user.id}?${params.toString()}`);

            if (response.data.success) {
                const records = response.data.data || [];
                console.log("TPN Records with files:", JSON.stringify(records, null, 2));
                setTpnRecords(records);
                // Set the first TPN record as active if available, otherwise reset activeTPN
                if (records.length > 0) {
                    setActiveTPN(records[0]);
                } else {
                    setActiveTPN(null);
                }
            } else {
                toast(<NotificationToast title="TPN Records Error" message={response.data.message || "Failed to load TPN records"} type="error" />);
            }
        } catch (error) {
            console.error("Error fetching TPN records:", error);
            toast(<NotificationToast title="TPN Records Error" message={error.response?.data?.message || "An error occurred while fetching TPN records."} type="error" />);
        } finally {
            setLoadingTpnRecords(false);
        }
    };

    // Load TPN records when component mounts or when user changes
    useEffect(() => {
        fetchTpnRecords();
    }, [current_booking?.user?.id]);

    // Pre-populate request notes when TPN records change
    useEffect(() => {
        if (tpnRecords.length > 0) {
            const updatedNotes = {};
            tpnRecords.forEach((tpn) => {
                if (tpn.request_notes) {
                    updatedNotes[tpn.id] = tpn.request_notes;
                }
            });
            setRequestNotes(updatedNotes);
        }
    }, [tpnRecords]);

    const tpn_checks = async (type) => {
        if (window.confirm("Running a TPN check will cost R 77.23. Are you sure that you want to continue?")) {
            setTpnLoading(true);
            try {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}tpn/consumer_enquiry/${current_booking.user.id}/${type}`, { booking_id: current_booking.id });

                if (response.data === "done") {
                    // Only refresh TPN records, don't dispatch booking update
                    await fetchTpnRecords();
                    toast(<NotificationToast title="Success" message="Credit check completed successfully" type="success" />);
                } else {
                    const msg = typeof response.data === "string" ? response.data : response.data?.message || "An issue occurred with the TPN check.";
                    const showList = typeof msg === "string" && msg.includes(",");
                    toast(<NotificationToast title="TPN Issue" message={msg} list={showList} type="error" />);
                }
            } catch (error) {
                const raw = error?.response?.data;
                // Prefer plain string body if present, otherwise message field, otherwise generic
                const msg = typeof raw === "string" ? raw : raw?.message || "An error occurred while processing the TPN check.";
                const showList = typeof msg === "string" && msg.includes(",");
                toast(<NotificationToast title="TPN Error" message={msg} list={showList} type="error" />);
            } finally {
                setTpnLoading(false);
            }
        }
    };

    // Format status for display
    const formatStatus = (status) => {
        if (!status) return "Not Checked";

        // Special case for pending_approval status
        if (status === "pending_approval") {
            return "Pending Approval";
        }

        return status
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    // Get status icon and color
    const getStatusInfo = (status) => {
        switch (status) {
            case "approved":
                return { icon: faCheckCircle, color: "text-green-500" };
            case "denied":
                return { icon: faTimesCircle, color: "text-red-500" };
            case "pending_approval":
                return { icon: faUserClock, color: "text-yellow-500" };
            default:
                return { icon: null, color: "text-gray-500" };
        }
    };

    // Handle opening file selection modal
    const handleOpenFileModal = (tpn) => {
        setActiveTPN(tpn);
        setFileModalContext({
            type: "user_tpn",
            id: tpn.id,
            booking_id: current_booking?.id,
            user_id: current_booking?.user?.id,
            current_booking: current_booking, // Pass the full current_booking object
        });
        setShowFileModal(true);
    };

    // Handle file attachment
    const handleAttachFiles = async (files) => {
        if (!activeTPN) return;

        try {
            const fileIds = files.map((file) => file.id);
            if (fileIds.length === 0) return;

            // Call the API to associate the files with the TPN record
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}tpn-approval/${activeTPN.id}/attach-files`,
                {
                    file_ids: fileIds,
                    booking_id: current_booking?.id,
                    user_id: current_booking?.user?.id,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "X-Requested-With": "XMLHttpRequest",
                        "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
                    },
                    withCredentials: true,
                },
            );

            if (response.data.success) {
                // Refresh TPN records to get the updated file associations
                await fetchTpnRecords();

                // Show success message
                toast(<NotificationToast title="Success" message={`${files.length} file(s) attached successfully`} type="success" />);

                // Reset file selection and close modal
                setSelectedFiles([]);
                setShowFileModal(false);
            } else {
                throw new Error(response.data.message || "Failed to attach files");
            }
        } catch (error) {
            console.error("Error attaching files:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to attach files. Please try again.";

            toast(<NotificationToast title="Error" message={errorMessage} type="error" />);
        } finally {
            // Always reset the active TPN after operation
            setActiveTPN(null);
        }
    };

    const requestApproval = async (tpnId) => {
        setTpnLoading(true);

        try {
            const formData = new FormData();
            formData.append("request_notes", requestNotes[tpnId] || "");

            // Append files if any are selected
            if (selectedFiles.length > 0) {
                selectedFiles.forEach((fileObj) => {
                    formData.append("files[]", fileObj.file);
                });
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}tpn-approval/${tpnId}/request`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (response.data.success) {
                toast(<NotificationToast title="Success" message="Approval requested successfully" type="success" />);
                setShowRequestNotes(null);
                // Clear notes for this specific TPN
                setRequestNotes({ ...requestNotes, [tpnId]: "" });
                // Only refresh TPN records, not the entire booking
                await fetchTpnRecords();
            } else {
                throw new Error(response.data.message || "Failed to request approval");
            }
        } catch (error) {
            console.error("Error requesting approval:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to request approval"} type="error" />);
        } finally {
            setTpnLoading(false);
        }
    };

    // File type to icon mapping
    const fileTypes = {
        pdf: faFilePdf,
        jpg: faFileImage,
        jpeg: faFileImage,
        png: faFileImage,
        gif: faFileImage,
        doc: faFileWord,
        docx: faFileWord,
        xls: faFileExcel,
        xlsx: faFileExcel,
        csv: faFileCsv,
        txt: faFileAlt,
        zip: faFileArchive,
        rar: faFileArchive,
        mp3: faFileAudio,
        wav: faFileAudio,
        mp4: faFileVideo,
        mov: faFileVideo,
    };

    return (
        <div className="space-y-3">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Credit Check</h3>
                <div className="flex space-x-2">
                    <button type="button" onClick={() => setShowQueueModal(true)} className="text-primary hover:text-primary-dark" title="View Credit Check Queue">
                        <FontAwesomeIcon icon={faList} className="h-5 w-5" />
                    </button>
                    <button
                        type="button"
                        onClick={fetchTpnRecords}
                        disabled={tpnLoading || checkingUpdates}
                        className="text-primary hover:text-primary-dark disabled:opacity-50"
                        title="Refresh Credit Check"
                    >
                        <FontAwesomeIcon icon={faSync} className={`h-5 w-5 ${checkingUpdates ? "animate-spin" : ""}`} />
                    </button>
                </div>
            </div>

            <div>
                <div className="flex space-x-1 mb-1">
                    {!current_staff.building_manager && (
                        <>
                            <CustomButton text="Consumer A" styling="w-full" icon={faUser} onClick={() => tpn_checks("consumer_a")} loading={tpnLoading} loadingtext="Please wait..." />

                            {current_booking.joint_application === 1 && current_staff.building_manager === 0 && (
                                <CustomButton text="Consumer B" styling="w-full" icon={faUserGroup} onClick={() => tpn_checks("consumer_b")} loading={tpnLoading} loadingtext="Please wait..." />
                            )}
                        </>
                    )}
                </div>

                {current_booking.company_application === 1 && !current_staff.building_manager && (
                    <div className="mt-2">
                        <CustomButton text="Company" styling="w-full" icon={faBuilding} onClick={() => tpn_checks("company")} loading={tpnLoading} loadingtext="Please wait..." />
                    </div>
                )}

                {!current_staff.building_manager && (
                    <div className="mt-2">
                        {/* Show dropdown if joint application or company application */}
                        {current_booking.joint_application === 1 || current_booking.company_application === 1 ? (
                            <div className="flex space-x-2">
                                <select
                                    value={manualReportType}
                                    onChange={(e) => setManualReportType(Number(e.target.value))}
                                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary focus:border-primary"
                                >
                                    <option value={0}>Applicant 1</option>
                                    {current_booking.joint_application === 1 && <option value={1}>Applicant 2</option>}
                                    {current_booking.company_application === 1 && <option value={2}>Company</option>}
                                </select>
                                <CustomButton
                                    text="Manual"
                                    styling="flex-1 bg-gray-600 hover:bg-gray-700"
                                    icon={faFileCirclePlus}
                                    onClick={() => createManualCreditCheck(manualReportType)}
                                    loading={creatingManual}
                                    loadingtext="Creating..."
                                />
                            </div>
                        ) : (
                            <CustomButton
                                text="Manual Credit Check"
                                styling="w-full bg-gray-600 hover:bg-gray-700"
                                icon={faFileCirclePlus}
                                onClick={() => createManualCreditCheck(0)}
                                loading={creatingManual}
                                loadingtext="Creating..."
                            />
                        )}
                    </div>
                )}

                {/* Affordability Calculator Button - QL Only */}
                {process.env.REACT_APP_COLOUR === "QL" && (
                    <div className="mt-2">
                        <CustomButton
                            text="Affordability Calculator"
                            styling="w-full bg-gray-600 hover:bg-gray-700"
                            icon={faCalculator}
                            onClick={() => {
                                dispatch(
                                    openModal({
                                        modal_type: "ModalAffordabilityCalculator",
                                        modal_props: {
                                            current_booking: current_booking,
                                            current_staff: current_staff,
                                            onSave: () => {
                                                // Refresh TPN records to show updated request_notes
                                                fetchTpnRecords();
                                            },
                                        },
                                    }),
                                );
                            }}
                            title="Affordability Calculator"
                        />
                    </div>
                )}
            </div>

            <div className="mt-5">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reports</h3>
                <div>
                    {loadingTpnRecords ? (
                        <div key="loading" className="flex justify-center py-4">
                            <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-primary text-xl" />
                            <span className="ml-2">Loading TPN records...</span>
                        </div>
                    ) : tpnRecords.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">No TPN records found</div>
                    ) : (
                        tpnRecords.map((tpn) => (
                            <div key={"tpn_" + tpn.id} className="bg-white overflow-hidden shadow rounded-lg border border-gray-200 mb-4">
                                <div className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <h4 className="text-sm font-medium text-gray-900">{tpn.report_type == 1 ? "Applicant 2" : tpn.report_type == 2 ? "Company" : "Applicant 1"}</h4>
                                            {tpn.enquiryId?.startsWith("MANUAL-") && <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800">Manual</span>}
                                        </div>
                                        <span
                                            className={`px-2 py-1 text-xs rounded-full ${
                                                tpn.status === "approved" ? "bg-green-100 text-green-800" : tpn.status === "denied" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"
                                            }`}
                                        >
                                            {formatStatus(tpn.status)}
                                        </span>
                                    </div>
                                    <div className="mt-1 text-xs text-gray-500">
                                        <p>Created: {moment(tpn.created_at).format("DD/MM/YYYY H:mm")}</p>
                                    </div>

                                    {tpn.credex_img && (
                                        <div className="mt-3">
                                            <img src={tpn.credex_img} alt="Credit report" className="w-full h-auto rounded border border-gray-200" />
                                        </div>
                                    )}

                                    {/* File Icons Mapping */}

                                    {/* Display attached files if any */}
                                    {tpn.attached_files && tpn.attached_files.length > 0 && (
                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                            <p className="text-xs font-medium text-gray-700 mb-2">Attached files:</p>
                                            <ul className="space-y-2">
                                                {tpn.attached_files.map((file) => {
                                                    // Safely get the file name from either file.name or file.file.name
                                                    const fileName = file.file?.name || file.name || "file";
                                                    const fileExt = fileName.split(".").pop()?.toLowerCase();
                                                    const fileIcon = fileExt ? fileTypes[fileExt] || faFile : faFile;

                                                    return (
                                                        <li key={file.id} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                                                            <div className="flex items-center min-w-0">
                                                                <FontAwesomeIcon icon={fileIcon} className="mr-2 text-gray-500" />
                                                                <span className="truncate max-w-xs" title={fileName}>
                                                                    {fileName}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <button
                                                                    type="button"
                                                                    className="ml-2 text-xs text-primary hover:text-primary_hover whitespace-nowrap"
                                                                    onClick={(e) => handleFilePreview(file.file?.id || file.id, e)}
                                                                >
                                                                    View
                                                                </button>

                                                                {tpn?.status === "pending" && (
                                                                    <button
                                                                        type="button"
                                                                        className="ml-2 text-red-500 hover:text-red-700"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            removeFile(file.id, tpn.id);
                                                                        }}
                                                                        title="Remove file"
                                                                    >
                                                                        <FontAwesomeIcon icon={faTimes} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}

                                    <div className="mt-3 flex space-x-3">
                                        {tpn.pdf_url && (
                                            <a href={tpn.pdf_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:text-primary_hover">
                                                View PDF
                                            </a>
                                        )}
                                        {tpn.report_url && (
                                            <a href={tpn.report_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:text-primary_hover">
                                                View Report
                                            </a>
                                        )}
                                    </div>

                                    <div className="mt-4 pt-4 border-t border-gray-100">
                                        {tpn.status === "pending" ? (
                                            <div className="space-y-2">
                                                {showRequestNotes !== tpn.id ? (
                                                    <>
                                                        <CustomButton
                                                            text="Request Approval"
                                                            styling="w-full"
                                                            icon={faPaperPlane}
                                                            onClick={() => handleOpenRequestNotes(tpn.id)}
                                                            loading={tpnLoading}
                                                            loadingtext="Please wait..."
                                                        />
                                                        <CustomButton
                                                            text="Delete"
                                                            styling="w-full bg-red-500 hover:bg-red-600 text-white"
                                                            icon={faTrash}
                                                            onClick={() => deleteTPN(tpn.id)}
                                                            loading={deletingTPN}
                                                            loadingtext="Deleting..."
                                                        />
                                                    </>
                                                ) : (
                                                    <div className="space-y-3">
                                                        <div className="space-y-3">
                                                            <div>
                                                                <label htmlFor="request-notes" className="block text-sm font-medium text-gray-700 mb-1">
                                                                    Additional Information (Optional)
                                                                </label>
                                                                <textarea
                                                                    id="request-notes"
                                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                                                                    rows="9"
                                                                    placeholder="Add any additional information for the approver..."
                                                                    value={requestNotes[tpn.id] || ""}
                                                                    onChange={(e) => setRequestNotes({ ...requestNotes, [tpn.id]: e.target.value })}
                                                                />
                                                            </div>

                                                            <div className="space-y-2">
                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Attach Files (Optional)</label>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleOpenFileModal(tpn)}
                                                                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                                                    >
                                                                        <FontAwesomeIcon icon={faUpload} className="mr-1.5 h-3.5 w-3.5 text-gray-600" />
                                                                        Select Files
                                                                    </button>
                                                                </div>

                                                                {tpn.files && tpn.files.length > 0 && (
                                                                    <div className="mt-2 space-y-2">
                                                                        <p className="text-xs font-medium text-gray-700">Attached files:</p>
                                                                        <ul className="space-y-1">
                                                                            {tpn.files.map((file) => {
                                                                                const ext = (file.name || "").split(".").pop()?.toLowerCase();
                                                                                return (
                                                                                    <li key={file.id} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                                                                                        <div className="flex items-center min-w-0">
                                                                                            <span className="truncate max-w-xs">{file.name}</span>
                                                                                            {ext && (
                                                                                                <span className="ml-2 px-1.5 py-0.5 text-[9px] leading-none rounded bg-gray-200 text-gray-700 uppercase">
                                                                                                    {ext}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        <div className="flex items-center">
                                                                                            <button
                                                                                                type="button"
                                                                                                className="ml-2 text-xs text-primary hover:text-primary_hover whitespace-nowrap"
                                                                                                onClick={(e) => handleFilePreview(file.id, e)}
                                                                                            >
                                                                                                View
                                                                                            </button>
                                                                                            {tpn.status === "pending" && (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="ml-2 text-red-500 hover:text-red-700"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        removeFile(file.id, tpn.id);
                                                                                                    }}
                                                                                                    title="Remove file"
                                                                                                >
                                                                                                    <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                    </li>
                                                                                );
                                                                            })}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                                <div className="flex space-x-2">
                                                                    <CustomButton text="Cancel" styling="w-1/2 bg-gray-200 hover:bg-gray-300 text-gray-800" onClick={() => setShowRequestNotes(null)} />
                                                                    <CustomButton
                                                                        text="Submit Request"
                                                                        styling="w-1/2"
                                                                        icon={faPaperPlane}
                                                                        onClick={() => requestApproval(tpn.id)}
                                                                        loading={tpnLoading}
                                                                        loadingtext="Submitting..."
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : ["in_progress", "pending_approval", "approved", "denied"].includes(tpn.status) ? (
                                            <div className="space-y-3">
                                                <div className="text-sm text-gray-600">
                                                    <p>
                                                        {tpn.status === "in_progress" ? (
                                                            <>
                                                                Assigned to{" "}
                                                                {tpn.assignee?.id ? (
                                                                    <a
                                                                        href={`http://localhost:3000/booking/management?tag=${tpn.assignee?.latest_booking?.tag || 0}&booking_id=${
                                                                            tpn.assignee?.latest_booking?.id || 0
                                                                        }&user_id=${tpn.assignee?.id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                                    >
                                                                        {tpn.assignee.name} {tpn.assignee.surname}
                                                                    </a>
                                                                ) : (
                                                                    "a team member"
                                                                )}
                                                            </>
                                                        ) : tpn.status === "pending_approval" ? (
                                                            <>
                                                                Waiting for{" "}
                                                                {tpn.assignee?.id ? (
                                                                    <a
                                                                        href={`http://localhost:3000/booking/management?tag=${tpn.assignee.booking_tag || 0}&booking_id=${
                                                                            tpn?.assignee?.booking?.id || 0
                                                                        }&user_id=${tpn.assignee?.id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                                    >
                                                                        {tpn.assignee.name} {tpn.assignee.surname}
                                                                    </a>
                                                                ) : (
                                                                    "reviewer"
                                                                )}{" "}
                                                                to review
                                                            </>
                                                        ) : tpn.status === "approved" ? (
                                                            <>
                                                                Approved by{" "}
                                                                {tpn.assignee?.id ? (
                                                                    <a
                                                                        href={`http://localhost:3000/booking/management?tag=${tpn.assignee?.latest_booking?.tag || 0}&booking_id=${
                                                                            tpn.assignee?.latest_booking?.id || 0
                                                                        }&user_id=${tpn.assignee?.id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                                    >
                                                                        {tpn.assignee.name} {tpn.assignee.surname}
                                                                    </a>
                                                                ) : (
                                                                    "a team member"
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                Denied by{" "}
                                                                {tpn.assignee?.id ? (
                                                                    <a
                                                                        href={`http://localhost:3000/booking/management?tag=${tpn.assignee?.latest_booking?.tag || 0}&booking_id=${
                                                                            tpn.assignee?.latest_booking?.id || 0
                                                                        }&user_id=${tpn.assignee?.id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                                    >
                                                                        {tpn.assignee.name} {tpn.assignee.surname}
                                                                    </a>
                                                                ) : (
                                                                    "a team member"
                                                                )}
                                                            </>
                                                        )}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-1">Updated on {moment(tpn.status_updated_at).format("DD/MM/YYYY [at] H:mm")}</p>
                                                    <p className="text-xs text-gray-500 mt-2">Request Notes:</p>
                                                    <pre>{tpn.request_notes}</pre>
                                                </div>

                                                {/* Action buttons for in-progress status */}
                                                {tpn.status === "in_progress" && (
                                                    <div className="space-y-2 mt-3">
                                                        <div className="flex space-x-2">
                                                            <CustomButton
                                                                text="Move to Approved"
                                                                styling="w-1/2 bg-green-500 hover:bg-green-600 text-white"
                                                                icon={faCheckCircle}
                                                                onClick={() => moveToApproved(tpn.id)}
                                                                loading={movingToApproved}
                                                                loadingtext="Moving..."
                                                            />
                                                            <CustomButton
                                                                text="Move to Denied"
                                                                styling="w-1/2 bg-red-500 hover:bg-red-600 text-white"
                                                                icon={faTimesCircle}
                                                                onClick={() => moveToDenied(tpn.id)}
                                                                loading={movingToDenied}
                                                                loadingtext="Moving..."
                                                            />
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <CustomButton
                                                                text="Re-send Approved Message"
                                                                styling="w-1/2 bg-blue-500 hover:bg-blue-600 text-white"
                                                                icon={faRedo}
                                                                onClick={() => resendApprovalMessage(tpn.id)}
                                                                loading={resendingApproval}
                                                                loadingtext="Sending..."
                                                            />
                                                            <CustomButton
                                                                text="Move Back to Pending"
                                                                styling="w-1/2 bg-orange-500 hover:bg-orange-600 text-white"
                                                                icon={faArrowLeft}
                                                                onClick={() => moveToPending(tpn.id)}
                                                                loading={movingToPending}
                                                                loadingtext="Moving..."
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Action buttons for pending_approval status */}
                                                {tpn.status === "pending_approval" && (
                                                    <div className="space-y-2 mt-3">
                                                        <div className="flex space-x-2">
                                                            <CustomButton
                                                                text="Move to Approved"
                                                                styling="w-1/2 bg-green-500 hover:bg-green-600 text-white"
                                                                icon={faCheckCircle}
                                                                onClick={() => moveToApproved(tpn.id)}
                                                                loading={movingToApproved}
                                                                loadingtext="Moving..."
                                                            />
                                                            <CustomButton
                                                                text="Move to Denied"
                                                                styling="w-1/2 bg-red-500 hover:bg-red-600 text-white"
                                                                icon={faTimesCircle}
                                                                onClick={() => moveToDenied(tpn.id)}
                                                                loading={movingToDenied}
                                                                loadingtext="Moving..."
                                                            />
                                                        </div>
                                                        <CustomButton
                                                            text="Move Back to Pending"
                                                            styling="w-full bg-orange-500 hover:bg-orange-600 text-white"
                                                            icon={faArrowLeft}
                                                            onClick={() => moveToPending(tpn.id)}
                                                            loading={movingToPending}
                                                            loadingtext="Moving..."
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ) : null}

                                        {tpn.status === "denied" && (
                                            <div className="mt-3">
                                                <p className="text-xs text-gray-500 mt-2">Denial Reason:</p>
                                                <p className="text-xs text-gray-500">{tpn.denial_reason}</p>
                                            </div>
                                        )}

                                        {/* Clone to Pending button for approved/denied status */}
                                        {(tpn.status === "approved" || tpn.status === "denied") && (
                                            <div className="mt-3 space-y-2">
                                                <CustomButton
                                                    text="Clone to Pending"
                                                    styling="w-full bg-blue-500 hover:bg-blue-600 text-white"
                                                    icon={faCopy}
                                                    onClick={() => cloneToPending(tpn.id)}
                                                    loading={cloningToPending}
                                                    loadingtext="Cloning..."
                                                />
                                                {/* Delete button for super users only */}
                                                {current_staff?.id === 1 && (
                                                    <CustomButton
                                                        text="Delete"
                                                        styling="w-full bg-red-500 hover:bg-red-600 text-white"
                                                        icon={faTrash}
                                                        onClick={() => deleteTPN(tpn.id)}
                                                        loading={deletingTPN}
                                                        loadingtext="Deleting..."
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    {/* Linked TPN Reports */}
                    {current_booking?.linked_tpn?.reverse().map((tpn) => (
                        <div key={"linked_tpn_" + tpn.id} className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
                            <div className="p-4">
                                <h4 className="text-sm font-medium text-gray-900">
                                    {tpn?.user_name} {tpn?.user_surname}
                                </h4>
                                <p className="text-xs text-gray-500 mt-1">
                                    {tpn?.type} • {moment(tpn.created_at).format("DD/MM/YYYY H:mm:ss")}
                                </p>

                                {tpn.credex_img && (
                                    <div className="mt-3">
                                        <img src={tpn.credex_img} alt="Credit report" className="w-full h-auto rounded border border-gray-200" />
                                    </div>
                                )}

                                <div className="mt-3 flex space-x-4">
                                    <a href={tpn.pdf_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:text-primary_hover">
                                        View PDF
                                    </a>
                                    <a href={tpn.report_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:text-primary_hover">
                                        View Report
                                    </a>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Credit Check Queue Modal */}
            {showQueueModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-medium">Credit Check Queue</h3>
                            <button onClick={() => setShowQueueModal(false)} className="text-gray-400 hover:text-gray-500">
                                <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto flex-1">
                            <CreditCheckQueue
                                currentStaff={current_staff}
                                onChatClick={(item) => {
                                    // This will be handled by the parent component
                                    setShowQueueModal(false);
                                    // The actual navigation will be handled by the parent component
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* File Selection Modal */}
            <FileSelectionModal
                isOpen={showFileModal}
                onClose={() => {
                    setShowFileModal(false);
                    setFileModalContext(null);
                }}
                context={fileModalContext}
                onAttach={(files) => {
                    handleAttachFiles(files);
                    setShowFileModal(false);
                    setFileModalContext(null);
                }}
            />
        </div>
    );
};

export default CreditCheck;
